import { Colors } from "@/enums/Colors";
import { LazoPaths } from "@/enums/LazoPaths";
import AsyncAuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AsyncAuthService";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import CustomAuthServiceV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/CustomAuthServiceV2";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { ActivityOnboarding } from "@/interfaces/onboarding-interfaces/ActivityOnboarding";
import { CompanyOnboarding } from "@/interfaces/onboarding-interfaces/CompanyOnboarding";
import { Onboarding } from "@/interfaces/onboarding-interfaces/Onboarding";
import { UserOnboarding } from "@/interfaces/onboarding-interfaces/UserOnboarding";
import LazoLeadService from "@/services/LazoLeadService";
import LazoUserService from "@/services/LazoUserService";
import OnboardingService from "@/services/OnboardingService";
import ZapierService from "@/services/ZapierService";
import { OAuthRedirectUser } from "@/types/OAuthRedirectUser";
import { SuccessfullyOnboarding } from "@/types/SuccessfullyOnboarding";
import { Box, BoxProps, SxProps, Typography, styled, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { OnboardingActivityForm } from "./OnboardingActivityForm";
import { OnboardingCompanyForm } from "./OnboardingCompanyForm";

export const textFieldStyles: SxProps = {
  "& .MuiInputBase-root": {
    borderRadius: ".75rem",
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${Colors.STONE_GRAY}`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${Colors.AMATIST_PURPLE}`,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: `solid 1px ${Colors.STROKE_FOR_HOVERS_6C6C77}`,
    },
    "&.Mui-disabled": {
      backgroundColor: Colors.ALMOST_WHITE,
    },
    "&.Mui-error": {
      backgroundColor: Colors.INPUTS_BACKGROUND_ON_ERROR,
      border: `solid 1px ${Colors.INPUTS_BACKGROUND_ON_ERROR}`,
    },
  },
  "& .MuiFormLabel-root": {
    color: Colors.LIGHT_GRAY_CDCDCD,
    "&.Mui-focused": {
      color: Colors.AMATIST_PURPLE,
    },
  },
};

interface ContainerProps extends BoxProps {
  meetsPasswordRequirement?: boolean;
}

export const Container = styled(Box)<ContainerProps>(({ theme, meetsPasswordRequirement }) => ({
  display: "flex",
  "&.main-container": {
    width: "100%",
    height: "100dvh",
    [theme.breakpoints.down("sm")]: {
      "& #generic-form-container": {
        width: "100%",
      },
      flexDirection: "column",
    },
  },
  "&.background-container": {
    width: "50%",
    backgroundColor: Colors.LAZO_PURPLE,
    [theme.breakpoints.down("sm")]: {
      padding: "0",
      height: "93px",
      position: "relative",
      width: "100%",
    },
  },
  "&.body": {
    zIndex: 1,
    width: "50%",
    display: "flex",
    backgroundColor: Colors.BLACK,
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      backgroundColor: Colors.PURPLE,
      overflow: "hidden",
    },
  },
  "&.form-container": {
    width: "98%",
    height: "97%",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: Colors.WHITE,
    borderRadius: "1rem",
    overflowY: "auto",
    margin: 0,
    padding: 0,
    marginTop: "1rem",
    [theme.breakpoints.down("md")]: {
      minHeight: "none",
      maxHeight: "100%",
      paddingLeft: "1rem",
      paddingRight: "1rem",
    },
    [theme.breakpoints.down("sm")]: {
      borderRadius: "0",
      width: "100%",
      height: "100%",
      marginTop: "0rem",
    },
  },
  "&.form-subcontainer": {
    padding: 2,
    width: "70%",
    flexDirection: "column",
    gap: "1rem",
    [theme.breakpoints.down("md")]: {
      width: "100%",
      padding: 0,
    },
  },
  "&.two-field-form-row": {
    justifyContent: "space-between",
    gap: "1rem",
    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
  },
  "&.two-field-form-row-responsive": {
    justifyContent: "space-between",
    gap: "1rem",
    [theme.breakpoints.down("md")]: {},
  },

  "&.terms-text-container": {
    flexDirection: "row",
    gap: ".25rem",
    alignItems: "center",
  },
  "&.polygons-container": {
    width: "50%",
    height: "100dvh",
    position: "absolute",
    flexDirection: "column",
    justifyContent: "space-between",
    backgroundColor: Colors.BLACK,
    [theme.breakpoints.down("sm")]: {
      display: "none",
      position: "relative",
    },
  },
  "&.password-requirements-container": {
    alignItems: "center",
    gap: ".5rem",
    flexDirection: "row",
  },
  "&.password-requirement": {
    backgroundColor: meetsPasswordRequirement ? Colors.GREEN_01A902 : Colors.RED,
    width: "1rem",
    height: "1rem",
    borderRadius: "50%",
  },
  "&.form-item": {
    width: "100%",
    flexDirection: "column",
  },
  "&.white-container": {
    backgroundColor: Colors.WHITE,
    height: "4rem",
  },
}));

export const Text = styled(Typography)(({ theme }) => ({
  "&.title": {
    fontSize: "1.5rem",
    fontWeight: 600,
    marginTop: "40px",
    marginBottom: "0rem",
    color: Colors.DARK_PURPLE,
    [theme.breakpoints.down("md")]: {
      marginTop: ".5rem",
      textAlign: "center",
    },
  },
  "&.gray-text": {
    color: Colors.LIGHT_GRAY_8A8E93,
    fontWeight: 500,
    fontSize: ".75rem",
    paddingLeft: ".875rem",
  },
  "&.checkbox-text": {
    color: Colors.BLACK_151517,
    fontSize: ".75rem",
    lineHeight: "1.125rem",
    fontWeight: 500,
  },
  "&.button-text": {
    color: Colors.LAZO_PURPLE,
    fontSize: ".875rem",
    fontWeight: "600",
    lineHeight: "1.25rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: ".75rem",
      width: "167.5px",
    },
  },
  "&.terms-text": {
    fontWeight: "600",
    cursor: "pointer",
    color: Colors.LAZO_PURPLE,
  },
  "&.activity-form-subtitle": {
    fontWeight: "bold",
    color: Colors.DARK_GREY_3E3E3E,
    fontSize: "0.75rem",
  },
}));

export const ButtonContainer = styled(Box)(({ theme }) => ({
  "&.one-button-container": {
    marginTop: "2rem",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingBottom: "1rem",
    [theme.breakpoints.down("md")]: {
      justifyContent: "center",
    },
  },
  "&.two-button-container": {
    marginTop: "2rem",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingBottom: "1rem",
    [theme.breakpoints.down("md")]: {
      flexDirecton: "column",
      justifyContent: "center",
    },
  },
}));

export const StyledImage = styled("img")(({ theme }) => ({
  "&.upper-curved-polygon": {
    alignSelf: "flex-start",
    justifySelf: "flex-start",
    position: "absolute",
    background: `linear-gradient(to bottom right, ${Colors.LAZO_PURPLE}, ${Colors.LAZO_PURPLE_DCAFFF})`,
  },
  "&.bottom-curved-polygon": {
    alignSelf: "flex-end",
    justifySelf: "flex-start",
    position: "absolute",
    background: `linear-gradient(to bottom right, ${Colors.LAZO_PURPLE}, ${Colors.LAZO_PURPLE_DCAFFF})`,
  },
  "&.sign-up-info": {
    zIndex: 1,
    position: "absolute",
    top: "90%",
    display: "none",
    right: "2%",
    cursor: "pointer",
    [theme.breakpoints.down("sm")]: {
      position: "absolute",
      bottom: "auto",
      top: "0%",
      height: "32px",
      width: "32px",
      right: 20,
    },
  },
}));

const LazoOnboarding = () => {
  const navigate = useNavigate();

  const { codeExternalId } = useParams();

  const [onboardingStepIndex, setOnboardingStepIndex] = useState<number>(0);
  const [onboardingForm, setOnboardingForm] = useState<Onboarding>({} as Onboarding);
  const [loading, setLoading] = useState<boolean>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [tenantExternalId, setTenantExternalId] = useState<string>("");
  const userComesFromProductPage = codeExternalId === "FROM-PRODUCT-PAGE";
  const userComesFromCheckoutPage = codeExternalId === "FROM-CHECKOUT-PAGE";

  useEffect(() => {
    if (codeExternalId === "FROM-PRODUCT-PAGE" || codeExternalId === "FROM-CHECKOUT-PAGE") {
      setOnboardingStepIndex(1);
    }
    const tenantExternalId = sessionStorage.getItem("tenantExternalId");
    if (tenantExternalId) {
      setTenantExternalId(tenantExternalId);
    }
  }, []);

  const handleContinue = () => {
    if (onboardingStepIndex < ONBOARDING_STEPS.length - 1) {
      setOnboardingStepIndex(onboardingStepIndex + 1);
    }
  };

  const navigateToPreviousStep = () => {
    if (onboardingStepIndex > 0) {
      setOnboardingStepIndex(onboardingStepIndex - 1);
    }
  };

  const setCompanyInformation = (companyInformation: CompanyOnboarding) => {
    setOnboardingForm({ ...onboardingForm, company: companyInformation });
  };

  const answerActivityForm = async (activityInformation: ActivityOnboarding) => {
    sessionStorage.setItem("userComesFromProductPage", "true");
    const CompanyData = JSON.parse(localStorage.getItem("TenantMemberships__CurrentTenantMembership"));
    sessionStorage.setItem("hasSkippedActivityForm", "false");
    sessionStorage.setItem("activityFromProductInfo", JSON.stringify(activityInformation));

    navigate(LazoPaths.DASHBOARD);

    // navigate(
    //   LazoPaths.CHECKOUT.replace(
    //     ":companyName",
    //     CompanyData?.tenant?.name,
    //   ).replace(
    //     ":annualExpenses",
    //     activityInformation?.expenses?.toString() || "0",
    //   ) + `${getFormattedUtmParams()}`,
    // );
  };

  const onboardUser = async (activityInformation: ActivityOnboarding, hasSkippedActivityForm: boolean) => {
    const CompanyData = JSON.parse(sessionStorage.getItem("companyInformation"));
    const LoggedUserData = JSON.parse(localStorage.getItem("TenantMemberships__CurrentTenantMembership"));

    let companyName: string;

    if (onboardingForm?.company?.companyName != undefined) {
      companyName = onboardingForm.company.companyName;
    } else if (userComesFromCheckoutPage && LoggedUserData?.tenant?.name) {
      companyName = LoggedUserData.tenant.name;
    } else if (CompanyData?.companyName != undefined) {
      companyName = CompanyData.companyName;
    } else {
      console.error("Company name could not be determined");
      return;
    }

    const annualExpenses = activityInformation?.expenses?.toString() || "0";

    // const path = LazoPaths.CHECKOUT.replace(
    //   ":companyName",
    //   companyName,
    // ).replace(":annualExpenses", annualExpenses || "0");
    handleOnClickReady();
  };

  const handleOnClickReady = async () => {
    const onboardedUserWithActivityFromProductPage = sessionStorage.getItem("onboardedUserWthActivityFromProductPage");

    const filledActivity = sessionStorage.getItem("activityOnboarding") == "true";
    if (!userComesFromProductPage && !onboardedUserWithActivityFromProductPage) {
      const user: UserOnboarding = JSON.parse(sessionStorage.getItem("userOnboarding") || "{}");
      sessionStorage.removeItem("utmParams");
      await registerUserAndAddToCompany(user);
    } else {
      //At this point the user and company is already created
      // saveInstantQuote(null, tenantExternalId);
      if (filledActivity) {
        navigate(LazoPaths.DASHBOARD);
      } else {
        answerActivityRegisterAndRedirect();
      }
    }
  };

  const registerUserAndAddToCompany = async (user: UserOnboarding) => {
    try {
      const company: CompanyOnboarding = JSON.parse(sessionStorage.getItem("companyInformation"));
      if (company.otherIndustry != null) {
        company.industry = company.otherIndustry;
        company.otherIndustry = null;
      }

      const activityInformation: ActivityOnboarding = JSON.parse(sessionStorage.getItem("activityOnboarding"));
      const hasSkippedActivityForm: boolean = sessionStorage.getItem("hasSkippedActivityForm") === "true";

      const onboardingForm: Onboarding = {
        user: user,
        company: company,
        activity: activityInformation,
        hasSkippedActivityForm: hasSkippedActivityForm,
      };
      setLoading(true);
      const onboardingResponse = await OnboardingService.onboardUserAndCreateCompany(onboardingForm);

      onboardingResponse.onSuccess(async (succesfullyOnboarding: SuccessfullyOnboarding) => {
        if (!userComesFromProductPage) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            email: user.email,
            userip: succesfullyOnboarding.userIp,
            firstname: succesfullyOnboarding.firstName,
            lastname: succesfullyOnboarding.lastName,
            event: succesfullyOnboarding.event,
          });
          console.log("[GA]", "Created account from IQ");
        }

        [
          "activityFromProductInfo",
          "userComesFromProductPage",
          "hasSkippedActivityForm",
          "activityOnboarding",
          "onboardedUserWthActivityFromProductPage",
          "companyInformation",
          "userOnboarding",
        ].forEach((item) => sessionStorage.removeItem(item));

        // await saveInstantQuote(succesfullyOnboarding.companyExternalId, null);

        // @ts-ignore
        await LazoLeadService.matchLeadToUser(user);
        await ZapierService.postCompany(succesfullyOnboarding.companyExternalId);

        if (!user.isOAuthSignUp) {
          const loginResponse = await CustomAuthServiceV2.validateLoginWithEmailAndPassword(user.email, user.password);

          return loginResponse.onSuccess(async (publicAuthenticationToken) => {
            await Promise.all([
              AsyncAuthService.storeUser(publicAuthenticationToken.user),
              AsyncAuthService.storeToken(publicAuthenticationToken.token, publicAuthenticationToken.expiresAt),
            ]);

            redirect();
          });
        } else {
          const OAuthUser: OAuthRedirectUser = JSON.parse(sessionStorage.getItem("oauth_user"));
          const r = await LazoUserService.getByCoreUserExternalId(OAuthUser.externalId);
          r.onSuccess(async (lu) => {
            await Promise.all([AsyncAuthService.storeToken(sessionStorage.getItem("temp_token")), AsyncAuthService.storeUser(lu.user)]);
            sessionStorage.removeItem("temp_token");
            sessionStorage.removeItem("oauth_user");
            redirect();
          });
          r.onError(() => {
            NotificationService.error("There was an unexpected error. Try again");
          });
        }
      });

      onboardingResponse.onError((message, apiResponse) => {
        NotificationService.error(message);
        setLoading(false);
      });
    } catch (error) {
      console.error("Error during registration and login:", error);
      NotificationService.error(error.message || "An error occurred during registration.");
      setLoading(false);
    }
  };

  const answerActivityRegisterAndRedirect = async () => {
    setLoading(true);
    const loggedUserExternalId = AuthService.getUser()?.externalId;

    let activityInformation: ActivityOnboarding = sessionStorage.getItem("activityFromProductInfo")
      ? JSON.parse(sessionStorage.getItem("activityFromProductInfo"))
      : {};
    activityInformation.companyExternalId = tenantExternalId;
    const response = await OnboardingService.answerActivityForm(loggedUserExternalId, activityInformation);
    response.onSuccess(async () => {
      [
        "activityFromProductInfo",
        "userComesFromProductPage",
        "hasSkippedActivityForm",
        "activityOnboarding",
        "onboardedUserWthActivityFromProductPage",
      ].forEach((item) => sessionStorage.removeItem(item));
      navigate(LazoPaths.DASHBOARD);
    });
    response.onError((message, apiResponse) => {
      [
        "activityFromProductInfo",
        "userComesFromProductPage",
        "hasSkippedActivityForm",
        "activityOnboarding",
        "onboardedUserWthActivityFromProductPage",
      ].forEach((item) => sessionStorage.removeItem(item));
      NotificationService.warn("User already filled the activity form");
      navigate(LazoPaths.DASHBOARD);
      setLoading(false);
    });
  };

  function redirect() {
    if (isMobile) {
      navigate(LazoPaths.NOT_SUPPORTED);
    } else {
      window.location.href = LazoPaths.DASHBOARD;
    }
  }

  const ONBOARDING_STEPS = [
    {
      key: "company",
      index: 0,
      component: (
        <OnboardingCompanyForm
          companyInformation={onboardingForm.company}
          handleContinue={handleContinue}
          setCompanyInformation={setCompanyInformation}
        />
      ),
    },
    {
      key: "activity",
      index: 1,
      component: (
        <OnboardingActivityForm
          activityInformation={onboardingForm.activity}
          handleContinue={userComesFromProductPage ? answerActivityForm : onboardUser}
          handlePrevious={navigateToPreviousStep}
          userComesFromProductPage={userComesFromProductPage}
          userComesFromCheckoutPage={userComesFromCheckoutPage}
        />
      ),
    },
  ];

  const form: JSX.Element = useMemo(() => {
    return ONBOARDING_STEPS.find((step, index) => onboardingStepIndex === index)?.component!;
  }, [onboardingStepIndex]);

  return <>{form}</>;
};

export default LazoOnboarding;
